// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — Top Bar
// Search trigger, notifications, collaboration indicators, theme toggle
// ═══════════════════════════════════════════════════════════════════════════════
import { useState, useRef, useEffect, CSSProperties } from 'react';
import { useApp } from '../../hooks/useAppState';
import { useDisplayNameLookup } from '../../hooks/useDisplayName';
import { theme } from '../../lib/theme';
import { Badge, Avatar } from '../shared';
import type { NotificationPreferences } from '../../types';

export function TopBar() {
  const { state, dispatch, isAdmin, currentTheme: t } = useApp();
  // Customer-role users get a minimal bar: no internal search, no analyst
  // presence, no internal notifications — those all surface staff data.
  const isCustomer = state.user.role === 'customer';
  const lookupName = useDisplayNameLookup();
  const [notifOpen, setNotifOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const prefsRef = useRef<HTMLDivElement>(null);

  const unread = state.notifications.filter(n => !n.read).length;

  // Active users in last 60s (presence)
  const activeUsers = state.presence.filter(p => p.userId !== state.user.id && Date.now() - p.lastSeen < 60000);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (prefsRef.current && !prefsRef.current.contains(e.target as Node)) setPrefsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const headerStyle: CSSProperties = {
    height: 48, background: t.bg2, borderBottom: `1px solid ${t.border}`,
    display: 'flex', alignItems: 'center', padding: '0 16px', gap: 8, flexShrink: 0,
  };

  return (
    <header style={headerStyle}>
      {/* Sidebar toggle */}
      <button
        onClick={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}
        style={{ background: 'none', border: 'none', color: t.textSecondary, cursor: 'pointer', fontSize: 16, padding: '4px 6px', borderRadius: 4 }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
      </button>

      {/* Search bar */}
      {!isCustomer && <div
        onClick={() => dispatch({ type: 'SET_SEARCH_OPEN', open: true })}
        style={{
          flex: 1, maxWidth: 480, height: 32, background: t.input, border: `1px solid ${t.border}`,
          borderRadius: 8, display: 'flex', alignItems: 'center', padding: '0 12px',
          gap: 8, cursor: 'pointer', fontSize: 12, color: t.textTertiary,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        <span>Search articles, runbooks, KQL queries...</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, background: t.bg, padding: '1px 6px', borderRadius: 4 }}>⌘K</span>
      </div>}

      <div style={{ flex: 1 }} />

      {/* Real-time collaboration presence indicators */}
      {!isCustomer && activeUsers.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 20, background: `${t.accent}12`, border: `1px solid ${t.accent}22` }}>
          {activeUsers.slice(0, 3).map(u => (
            <div key={u.userId} title={`${lookupName(u.userId, u.userName)} is on ${u.view}`} style={{ position: 'relative' }}>
              <Avatar name={lookupName(u.userId, u.userName)} size={20} id={u.userId} />
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: 6, height: 6, borderRadius: '50%', background: t.green, border: `1px solid ${t.bg2}` }} />
            </div>
          ))}
          {activeUsers.length > 3 && <span style={{ fontSize: 9, color: t.accent, fontWeight: 700 }}>+{activeUsers.length - 3}</span>}
          <span style={{ fontSize: 10, color: t.accent, marginLeft: 2 }}>{activeUsers.length} online</span>
        </div>
      )}

      {/* Theme toggle */}
      <button
        onClick={() => {
          const next = state.themeSchedule.mode === 'dark' ? 'light' : state.themeSchedule.mode === 'light' ? 'auto' : 'dark';
          // Persist (same write SET_THEME_MODE performs), then reload: module-
          // scope style constants captured the previous palette at import time,
          // and only a fresh module evaluation restyles everything consistently.
          try {
            localStorage.setItem('mxdr365_theme', JSON.stringify({ ...state.themeSchedule, mode: next }));
          } catch { /* ignore */ }
          window.location.reload();
        }}
        title={`Theme: ${state.themeSchedule.mode} (click to cycle)`}
        style={{ background: 'none', border: `1px solid ${t.border}`, color: t.textSecondary, cursor: 'pointer', fontSize: 14, padding: '4px 8px', borderRadius: 6 }}
      >
        {state.themeSchedule.mode === 'dark' ? '🌙' : state.themeSchedule.mode === 'light' ? '☀️' : '🕐'}
      </button>

      {/* Admin badge */}
      {isAdmin && <Badge color={t.yellow}>Admin View</Badge>}

      {/* Notification prefs */}
      {!isCustomer && <div ref={prefsRef} style={{ position: 'relative' }}>
        <button
          onClick={() => { setPrefsOpen(!prefsOpen); setNotifOpen(false); }}
          title="Notification preferences"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '4px 8px', borderRadius: 4, color: t.textSecondary }}
        >
          ⚙️
        </button>
        {prefsOpen && <NotificationPreferencesPanel onClose={() => setPrefsOpen(false)} />}
      </div>}

      {/* Notifications */}
      {!isCustomer && <div ref={notifRef} style={{ position: 'relative' }}>
        <button
          onClick={() => { setNotifOpen(!notifOpen); setPrefsOpen(false); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '4px 8px', borderRadius: 4, color: t.textSecondary, position: 'relative' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          {unread > 0 && (
            <span style={{ position: 'absolute', top: 0, right: 2, background: t.red, color: '#fff', fontSize: 8, fontWeight: 700, width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {unread}
            </span>
          )}
        </button>

        {notifOpen && (
          <div style={{
            position: 'absolute', right: 0, top: '100%', marginTop: 8,
            width: 320, maxHeight: 400, overflowY: 'auto',
            background: t.card, border: `1px solid ${t.border}`,
            borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 999,
          }}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: t.text }}>Notifications</span>
              {unread > 0 && (
                <button onClick={() => dispatch({ type: 'READ_ALL_NOTIFICATIONS' })} style={{ background: 'none', border: 'none', color: t.accent, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Mark all read
                </button>
              )}
            </div>
            {state.notifications.map(n => (
              <div
                key={n.id}
                onClick={() => {
                  dispatch({ type: 'READ_NOTIFICATION', notifId: n.id });
                  // Task notifications carry entityType === 'task' (server-
                  // side createNotification in api/functions/projects.ts).
                  // Deep-link straight into the Task Drawer over the Tasks
                  // page so the user lands on the task they were paged about.
                  if (n.entityType === 'task' && n.entityId) {
                    dispatch({ type: 'SELECT_PROJECT_TASK', id: n.entityId });
                    dispatch({ type: 'SET_VIEW', view: 'tasks' });
                    setNotifOpen(false);
                  }
                }}
                style={{ padding: '10px 14px', borderBottom: `1px solid ${t.border}`, cursor: 'pointer', background: n.read ? 'transparent' : `${t.accent}08` }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 11, color: t.text }}>{n.title}</span>
                  {!n.read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: t.accent }} />}
                </div>
                <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 2 }}>{n.body}</div>
                <div style={{ fontSize: 9, color: t.textTertiary, marginTop: 4 }}>{new Date(n.createdAt).toLocaleString()}</div>
              </div>
            ))}
            {state.notifications.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: t.textTertiary, fontSize: 12 }}>No notifications</div>
            )}
          </div>
        )}
      </div>}

      {/* Sign out — visible to ALL roles (staff + customer). The only signpost
          for B2B guests, who otherwise have no way to end their session.
          Navigates to /logout, wired in staticwebapp.config.json → /.auth/logout. */}
      <button
        onClick={() => { window.location.href = '/logout'; }}
        title="Sign out"
        aria-label="Sign out"
        style={{ background: 'none', border: `1px solid ${t.border}`, color: t.textSecondary, cursor: 'pointer', padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
      </button>
    </header>
  );
}

// ─── Notification Preferences Panel ───
function NotificationPreferencesPanel({ onClose }: { onClose: () => void }) {
  const { state, dispatch, currentTheme: t } = useApp();
  const prefs = state.notificationPrefs;

  function update(partial: Partial<NotificationPreferences>) {
    dispatch({ type: 'UPDATE_NOTIFICATION_PREFS', prefs: partial });
  }

  const CATEGORY_LABELS: Record<string, string> = {
    ticket_created: 'Ticket Created', ticket_assigned: 'Ticket Assigned',
    ticket_resolved: 'Ticket Resolved', ticket_escalated: 'Ticket Escalated',
    article_verified: 'Article Verified', article_stale: 'Article Stale',
    handoff_posted: 'Handoff Posted', task_assigned: 'Task Assigned',
    task_overdue: 'Task Overdue', training_due: 'Training Due',
    mention: '@Mentions', system: 'System Alerts',
  };

  return (
    <div style={{
      position: 'absolute', right: 0, top: '100%', marginTop: 8, width: 340,
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
      boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 1000, padding: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: t.text }}>Notification Preferences</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.textTertiary, cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>

      {/* Channels */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: t.textTertiary, letterSpacing: 0.5, marginBottom: 8 }}>CHANNELS</div>
        {(['inApp', 'email', 'teams'] as const).map(ch => {
          const labels: Record<string, string> = { inApp: '🔔 In-App', email: '📧 Email', teams: '💬 Teams' };
          return (
            <div key={ch} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
              <span style={{ fontSize: 12, color: t.text }}>{labels[ch]}</span>
              <div
                onClick={() => update({ channels: { ...prefs.channels, [ch]: !prefs.channels[ch] } })}
                style={{ width: 32, height: 18, borderRadius: 9, cursor: 'pointer', position: 'relative', background: prefs.channels[ch] ? t.accent : t.border, transition: 'background .2s' }}
              >
                <div style={{ position: 'absolute', top: 2, left: prefs.channels[ch] ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left .2s' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Categories */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: t.textTertiary, letterSpacing: 0.5, marginBottom: 8 }}>CATEGORIES</div>
        <div style={{ maxHeight: 180, overflowY: 'auto' }}>
          {Object.entries(prefs.categories).map(([key, val]) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
              <span style={{ fontSize: 11, color: t.textSecondary }}>{CATEGORY_LABELS[key] || key}</span>
              <input
                type="checkbox"
                checked={val}
                onChange={() => update({ categories: { ...prefs.categories, [key]: !val } })}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Quiet hours */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: t.text }}>🌙 Quiet Hours</span>
          <div
            onClick={() => update({ quietHoursEnabled: !prefs.quietHoursEnabled })}
            style={{ width: 32, height: 18, borderRadius: 9, cursor: 'pointer', position: 'relative', background: prefs.quietHoursEnabled ? t.accent : t.border, transition: 'background .2s' }}
          >
            <div style={{ position: 'absolute', top: 2, left: prefs.quietHoursEnabled ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left .2s' }} />
          </div>
        </div>
        {prefs.quietHoursEnabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label style={{ fontSize: 10, color: t.textTertiary }}>
              From
              <input type="time" value={prefs.quietHoursStart} onChange={e => update({ quietHoursStart: e.target.value })} style={{ display: 'block', width: '100%', padding: '4px 8px', marginTop: 2, background: t.input, border: `1px solid ${t.border}`, borderRadius: 4, color: t.text, fontFamily: 'inherit', fontSize: 11 }} />
            </label>
            <label style={{ fontSize: 10, color: t.textTertiary }}>
              To
              <input type="time" value={prefs.quietHoursEnd} onChange={e => update({ quietHoursEnd: e.target.value })} style={{ display: 'block', width: '100%', padding: '4px 8px', marginTop: 2, background: t.input, border: `1px solid ${t.border}`, borderRadius: 4, color: t.text, fontFamily: 'inherit', fontSize: 11 }} />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}
